/**
 * Gate + data for the nav discovery menu (Figma 4327-59773) — the caret dropdown
 * that offers circles to viewers who belong to NONE.
 *
 * Data is LIVE: `circlesForOnboarding` returns one option per open room
 * (beta-2 fixed roster — mixed keys 11–92, tenured 101–103), already ranked
 * per-viewer and capacity-filtered by Brooklyn. This hook then applies the F1
 * tenured display gate, reorders tenured-first, and caps the mixed tail for
 * display. Joining goes through `assignToCircle(rainbowCategoryKey)` with the
 * circle key — per-room idempotent, capacity-enforced and race-safe.
 *
 * Name/description are server-authored per room (the DB row is the copy
 * source); options without a usable description are dropped entirely
 * (`filterDescribedOptions`) rather than given invented client copy.
 *
 * Data rides `services/community/circles` (the barrel) via `useNavMenuOffer` —
 * a deliberate SIBLING of `useOnboardingCircles`, which wraps the same two
 * operations but adds a settle-before-render retry policy (`OFFER_RETRY_*`,
 * the `OFFER_PENDING` sentinel through `error`) built for onboarding's race
 * against the un-awaited `/v5/user/onboard` POST. The nav menu renders on
 * every page long after onboarding, has no such race, and its `!error` guard
 * would read that sentinel as "unknown" and hide the menu. This hook keeps
 * only the GATE math, the join bookkeeping, and the presentation mapping —
 * the wire shape and its null defenses live in gql/adapters behind the
 * services boundary (the anti-corruption layer), so a schema change lands in
 * exactly one mapping.
 *
 * Gating is a cohort RELEASE gate, not an experiment — so there is deliberately
 * no `experiment_exposure` capture here.
 */
import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Map as ImmutableMap } from 'immutable'
import { usePostHogEvent } from '../../../PostHog/usePostHogEvent'
import { getLogger } from '../../../../log/index'
import { getBoundActions } from '../../../../actions/index'
import { useNavMenuOffer } from '../../../../services/community/circles/index'
import type { AssignCircleErrorKind } from '../shared/types'
import { filterDescribedOptions, filterTenuredOptions, isTenuredKey } from '../shared/circleKeys'
import useCirclesActiveCohort from '../Gating/useCirclesActiveCohort'
import { NAV_MENU_CAPACITY, NAV_MENU_MAX_MIXED_CARDS } from './navMenuCategoryCopy'
import type { NavMenuOption } from './navMenuCategoryCopy'

/**
 * The notice reuses the flag `Membership/WhatAreCirclesNotice.tsx` already owns,
 * so dismissing the explainer in the nav menu also hides it on the drawer's
 * Overview tab. Same copy, same promise — "do not show me this again".
 */
const NOTICE_TRACKING_KEY = 'communityWhatAreCirclesMessage'

/**
 * Server-persisted feature-tracking key for the "I am not interested in joining
 * a circle" opt-out (Brooklyn `user_features.community_circle_join_offer_dismissed`,
 * migration 00550; registered web-side in `lib/services/feature-tracking/index.js`
 * SCHEMA + createModel). Replaced the interim `circlesNavMenuDismiss` localStorage
 * shim — pre-migration opt-outs do not carry over (the small beta cohort
 * re-dismisses once, and the dismissal now follows the account across devices).
 */
const OPT_OUT_TRACKING_KEY = 'communityCircleJoinOfferDismissed'

/**
 * Per-kind toast copy for a failed join: `full` = the join lost the race for
 * the last seat; `limit` = the beta-2 membership cap (the viewer's own state,
 * not the circle's). The limit copy deliberately names no number — the cap
 * (MAX_ROSTER_MEMBERSHIPS) is Brooklyn's constant and the copy must not drift
 * from it. Deferred-i18n (unlaunched Circles beta) — see the COPY block in
 * CirclesNavMenu.tsx.
 */
const JOIN_ERROR_TOASTS: Record<AssignCircleErrorKind, string> = {
  full: 'Circle is full',
  limit: 'You are already in the maximum number of circles. Leave one to join another.',
  closed: 'This circle is not open for joining right now.',
  generic: 'Something went wrong joining the circle. Please try again.',
}

interface FeatureTrackingState {
  featureTracking?: ImmutableMap<string, unknown>
}

/** Read a feature-tracking boolean, coercing at the selector boundary so an
 *  unrelated featureTracking dispatch doesn't re-render the nav entry. */
const useTrackingFlag = (key: string): boolean => useSelector((s: FeatureTrackingState) => {
  if (!s.featureTracking || !ImmutableMap.isMap(s.featureTracking)) return false
  return Boolean(s.featureTracking.getIn(['data', key]))
})

export interface UseCirclesNavMenuParams {
  /**
   * `useCirclesEnabled({ requireDesktop: true }).enabled` — config killswitch AND
   * `community-circles-beta` AND a desktop UA. The join offer is desktop-only;
   * member surfaces (the drawer) stay device-independent and use the plain gate.
   */
  enabled: boolean
  isLoggedIn: boolean
  jwt: string
  /** Viewer's joined-circle count from the LIVE `useMyCircles`. */
  circleCount: number
  /** `useMyCircles` initial-load flag. */
  loading: boolean
  /** `useMyCircles` error, if any. */
  error: Error | null
}

export interface UseCirclesNavMenuResult {
  /** True when this viewer should get the discovery menu instead of the drawer. */
  showMenu: boolean
  options: NavMenuOption[]
  joinedIds: ReadonlySet<string>
  /** Resolves to the joined circle's feed id, or null when the join failed. */
  join: (rainbowCategoryKey: number) => Promise<string | null>
  /** True while a join is in flight — the menu disables every card's CTA. */
  joinPending: boolean
  noticeDismissed: boolean
  dismissNotice: () => void
  optOut: () => void
}

export default function useCirclesNavMenu ({
  enabled, isLoggedIn, jwt, circleCount, loading, error,
}: UseCirclesNavMenuParams): UseCirclesNavMenuResult {
  const { tenuredVisible, hasMenuAccess } = useCirclesActiveCohort()

  const dispatch = useDispatch()
  // `getBoundActions` returns a fresh reference each call; memoize so the
  // dismiss callbacks keep a stable identity across renders.
  const boundActions = useMemo(() => getBoundActions(dispatch), [dispatch])

  // A failed persist used to be COMPLETELY invisible (every layer swallows
  // it), which turns "I dismissed this" into "it came back next session" with
  // zero signal anywhere. The thunk now surfaces the API layer's boolean;
  // breadcrumb the failure. Deliberately NO `err` key — diagnostic, not pager
  // material (the `err` property is what routes a record to noticeError).
  const logPersistFailure = useCallback((key: string) => {
    getLogger()?.error({
      message: 'Circles nav-menu feature-tracking persist failed',
      trackingKey: key,
    })
  }, [])

  // ---- the notice: server-persisted today -------------------------------
  const noticeDismissed = useTrackingFlag(NOTICE_TRACKING_KEY)
  const trackNoticeDismissed = usePostHogEvent('circlesNavMenuNoticeDismissed')
  const dismissNotice = useCallback(() => {
    trackNoticeDismissed()
    // Updates the store immediately (hides the card) and PATCHes to Brooklyn
    // when a jwt exists; logged-out viewers just lose the dismissal.
    boundActions.featureTracking.setFeatureTrackingDataPersistent({
      data: ImmutableMap({ [NOTICE_TRACKING_KEY]: true }),
    }).then((saved: boolean) => {
      if (!saved) logPersistFailure(NOTICE_TRACKING_KEY)
    }).catch(() => {})
  }, [boundActions, trackNoticeDismissed, logPersistFailure])

  // ---- the opt-out: server-persisted (rides the SSR feature-tracking hydrate,
  // so it arrives with the page — no post-mount flash) --------------------
  const optedOut = useTrackingFlag(OPT_OUT_TRACKING_KEY)
  const trackOptedOut = usePostHogEvent('circlesNavMenuOptedOut')
  const optOut = useCallback(() => {
    // The opt-out is a permanent, cross-device product signal — track it, or
    // the beta's opt-out rate is unmeasurable.
    trackOptedOut()
    // Deliberately its OWN patch, never batched with the notice key: Brooklyn
    // validates with allowUnknown: false and rejects the whole request on one
    // unknown key — batching would let a future unknown key take this dismissal
    // down with it, silently (every layer swallows the failure).
    boundActions.featureTracking.setFeatureTrackingDataPersistent({
      data: ImmutableMap({ [OPT_OUT_TRACKING_KEY]: true }),
    }).then((saved: boolean) => {
      if (!saved) logPersistFailure(OPT_OUT_TRACKING_KEY)
    }).catch(() => {})
  }, [boundActions, trackOptedOut, logPersistFailure])

  // ---- the offer -------------------------------------------------------
  // Gate the query on everything cheap FIRST so a viewer outside the cohort, a
  // member, or an opted-out viewer never hits Brooklyn for an offer they cannot
  // see. `circleCount > 0` is deliberately part of the skip: members get the
  // drawer, not the menu.
  const offerSkip = !hasMenuAccess || !enabled || !isLoggedIn
    || optedOut || loading || !!error || circleCount > 0
  // Fetch + mutate live behind the services barrel — the wire shape and its
  // null defenses stay in gql/adapters, so this hook only maps the domain
  // options onto the menu's presentation shape (client-side copy + capacity).
  const {
    options: offerOptions, loading: offerLoading, join: joinOffer,
    refetchOffer, offerErrored,
  } = useNavMenuOffer({ jwt, skip: offerSkip })

  // One failed offer fetch would otherwise blank the menu for the whole SPA
  // session: the entry hides when the query settles errored-empty, the
  // RetryLink never retries network-level failures, and no surface remains to
  // click. Retry on the one free browser signal that the likely cause cleared
  // — connectivity returning. A retry that fails again just re-arms this.
  useEffect(() => {
    if (offerSkip || !offerErrored) return undefined
    window.addEventListener('online', refetchOffer)
    return () => window.removeEventListener('online', refetchOffer)
  }, [offerSkip, offerErrored, refetchOffer])

  const options = useMemo<NavMenuOption[]>(() => {
    // F1 tenured display gate — only the wave-1 'test' variant sees rooms keyed
    // >= TENURED_KEY_MIN ('participant' viewers get menu access with an empty
    // tenured partition, so the panel's ungrouped-list branch renders); shared
    // verbatim with the onboarding surface via the same helpers. Undescribed
    // rows (mis-provisioned) are dropped rather than given invented copy.
    const visible = filterDescribedOptions(filterTenuredOptions(offerOptions, tenuredVisible))
    // F2 — tenured rooms sort first for the flagged cohort; server order
    // preserved within each partition (the server already ranks mixed rooms by
    // viewer affinity, and tenured rooms always arrive last from the server,
    // so this is a display-order decision, not a data change). The cap trims
    // only the mixed tail — see NAV_MENU_MAX_MIXED_CARDS. The panel renders the
    // result as ONE unlabeled list: this ordering is all that survives of the
    // removed "For longtime members" / "By interest" grouping.
    const tenured = visible.filter((option) => isTenuredKey(option.rainbowCategoryKey))
    const mixed = visible.filter((option) => !isTenuredKey(option.rainbowCategoryKey))
    return [...tenured, ...mixed.slice(0, NAV_MENU_MAX_MIXED_CARDS)].map((option) => ({
      rainbowCategoryKey: option.rainbowCategoryKey,
      // Server-authored name/description (the DB row is the copy source;
      // filterDescribedOptions narrowed description to a present string).
      name: option.name,
      description: option.description,
      memberCount: option.memberCount,
      // Capacity keeps a fallback: absent = pre-Phase-B gateway tolerance.
      capacity: option.capacity ?? NAV_MENU_CAPACITY,
    }))
  }, [offerOptions, tenuredVisible])

  const [joinedIds, setJoinedIds] = useState<ReadonlySet<string>>(() => new Set<string>())
  // Single-flight join. The ref is the synchronous guard (two clicks in the
  // same tick both read stale state); the state drives the disabled CTAs.
  // Without it, concurrent assignToCircle mutations each run the success
  // hand-off — duplicate history.push, duplicate circles_member_joined,
  // duplicate refetches — and a second category clicked mid-flight can show
  // two "Joined" cards for a viewer who is in one circle.
  const joinPendingRef = useRef(false)
  const [joinPending, setJoinPending] = useState(false)
  const join = useCallback(async (rainbowCategoryKey: number): Promise<string | null> => {
    if (joinPendingRef.current) return null
    joinPendingRef.current = true
    setJoinPending(true)
    try {
      // Never throws — failure (including the resolved-but-null shape)
      // resolves with a null feedId plus a classified error kind.
      const result = await joinOffer(rainbowCategoryKey)
      // Mark joined ONLY when the server returned the joined feed (`'error' in
      // result` is the union's discriminant, mirroring `AssignCircleResult`):
      // marking a failure would strand a disabled "Joined" card with no retry
      // path while the caller correctly treats null as failure — Community
      // rule 8: stay in the current state on failure.
      if (!('error' in result)) {
        setJoinedIds((prev) => {
          const next = new Set(prev)
          next.add(String(rainbowCategoryKey))
          return next
        })
      } else {
        // The offer is read at query time but the join is decided at join time,
        // so a pool that filled in between rejects here (Brooklyn's
        // `claimCircleSeat` → "Circle is full") — as does a viewer who hit the
        // beta-2 membership cap. Toast per classified kind rather than fail
        // silently: the CTA stays enabled for a retry, so with no feedback it
        // reads as a dead button. This MUST live here, not in the caller — the
        // caller sees the same `null` for the single-flight suppression above,
        // and toasting there would fire on an ordinary double-tap.
        // Deferred-i18n (unlaunched Circles beta), matching the menu's COPY.
        boundActions.toasty.addToasty(JOIN_ERROR_TOASTS[result.error])
      }
      return result.feedId
    } finally {
      joinPendingRef.current = false
      setJoinPending(false)
    }
  }, [joinOffer, boundActions])

  // The joined overlay EXPIRES once live membership confirms the join
  // (`circleCount` settles above zero — at which point `showMenu` is false, so
  // the clear is never visible). The overlay's only job is the immediate
  // "Card Updates to Joined" feedback in the window before `refetchCircles`
  // lands; without this expiry it is write-only, and a later LEAVE re-opens
  // the menu (circleCount back to 0) with a stale disabled "Joined" card —
  // the tester-reported dead end. The journey's Circle Leave flow requires
  // the card back in its Join state ("Spot Becomes Available → User May
  // Rejoin"). Clearing on membership-confirmed (rather than on settled-empty)
  // deliberately keeps the overlay alive while the post-join refetch is still
  // in flight or has failed — the viewer IS a member then, so "Joined" is the
  // truthful render if the menu is somehow still up. Identity-bail (`prev`
  // returned unchanged) keeps the no-op case render-free.
  // `joinedIds` in the deps makes the expiry SELF-HEALING rather than
  // transition-triggered: an overlay write landing after membership already
  // settled >0 (a future refetch path, window-focus refetch, a second join
  // surface) still re-runs the clear instead of stranding a stale card until
  // the next membership transition. Loop-safe — the identity bail terminates
  // the re-run chain.
  useEffect(() => {
    if (loading || error || circleCount === 0) return
    setJoinedIds((prev) => (prev.size ? new Set<string>() : prev))
  }, [loading, error, circleCount, joinedIds])

  // An empty offer is Brooklyn's documented "hide the offer" contract (every
  // pool full, or the viewer is not eligible), so the menu must not render an
  // empty shell. `offerLoading` is first-fetch-only (the services hook defines
  // it as `loading && !data` per the slice-hook convention), so the icon stays
  // hidden until the first result rather than flashing an empty panel.
  // NOTE: deliberately does NOT gate on the offer query's error. It runs with
  // errorPolicy 'all', which exists so a PARTIAL error arrives alongside usable
  // data — treating any error as fatal would blank a fully-populated menu, and
  // silently, since Apollo reports it as data + error rather than throwing.
  // `options.length > 0` already covers the error-with-no-data case. This is the
  // opposite call from `useMyCircles`'s `error` above on purpose: there a
  // partial error must read as "unknown" so we never show a join pitch to a real
  // member; here the worst case is a slightly stale member count.
  const offerReady = !offerSkip && !offerLoading && options.length > 0

  // `circleCount === 0` is NOT the same as "this user is in no circle" — the
  // list is also empty pre-hydration, when the gate is off, when logged out, and
  // while the query is in flight. `errorPolicy: 'all'` additionally means a
  // partial Brooklyn error arrives WITH data, so a non-null error must read as
  // "unknown", never "zero circles" — otherwise a blip shows the join pitch to
  // actual members.
  const showMenu = hasMenuAccess
    && enabled
    && isLoggedIn
    && !loading
    && !error
    && circleCount === 0
    && !optedOut
    && offerReady

  return {
    showMenu,
    options,
    joinedIds,
    join,
    joinPending,
    noticeDismissed,
    dismissNotice,
    optOut,
  }
}
